import { User, UserKind } from "01Data/Account";
import { AppContext } from "01Data/AppData";
import { BuildingInfo } from "01Data/DataTypes";
import PageTemplate from "04UserPages/00PageTemplate/PageTemplate";
import {
  BreadCrumbItem,
  BreadCrumbList,
} from "04UserPages/99Parts/BreadCrumbList";
import { ConfirmDialog } from "04UserPages/99Parts/ConfirmDialog";
import { NoteText } from "97Parts/Styles";
import { useMessageDialog } from "97Parts/useMessageDialog";
import { useModel } from "99Tools/useModel";
import { useWindowSize } from "99Tools/useWindowSize";
import { AddCircle } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  styled,
} from "@mui/material";
import { LIST_HEADER_COLOR } from "Settings";
import { validate } from "email-validator";
import { useContext, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";

const ColorHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: LIST_HEADER_COLOR,
  color: theme.palette.common.white,
}));

const StatickyCell = styled(TableCell)(({ theme }) => ({
  position: "sticky",
  left: 0,
  background: "#fff",
  zIndex: 999,
}));

const FormDlg = styled("form")(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: "100%", // Fix IE 11 issue.
}));

const ErrorText = styled("div")(({ theme }) => ({
  color: "#f00",
}));

const ButtonDlg = styled(LoadingButton)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
}));

const ButtonChecked = styled(LoadingButton)(({ theme }) => ({
  color: theme.palette.common.white,
  border: "solid 1px",
  backgroundColor: "#009944",
  "&:hover": {
    color: theme.palette.common.white,
    border: "solid 1px",
    backgroundColor: "#69BD83",
  },
}));

const ButtonUnChecked = styled(LoadingButton)(({ theme }) => ({
  border: "solid 1px",
  borderColor: "#009944",
  "&:hover": {
    border: "solid 1px",
    borderColor: "#69BD83",
  },
}));

/**
 * カラム設定
 */
const columns = [
  {
    id: "id",
    label: "アカウント名",
    minWidthPC: 150,
    minWidthSmartphone: "15vw",
  },
  { id: "name", label: "利用者", minWidthPC: 150, minWidthSmartphone: 120 },
  { id: "kind", label: "権限", minWidthPC: 150, minWidthSmartphone: "15vw" },
  {
    id: "building",
    label: "建物テンプレ",
    minWidthPC: 50,
    minWidthSmartphone: "5vw",
  }, // 数は可変
  { id: "memo", label: "備考", minWidthPC: 150, minWidthSmartphone: "15vw" },
];

const AccountTypeMenus = (accountType: UserKind) => {
  if (accountType === "administrator") {
    return [
      <MenuItem key="sub_admin" value="sub_admin">
        <div>sub_admin</div>
      </MenuItem>,
      <MenuItem key="manager" value="manager">
        <div>manager</div>
      </MenuItem>,
      <MenuItem key="user" value="user">
        <div>user</div>
      </MenuItem>,
    ];
  } else if (accountType === "sub_admin") {
    return [
      <MenuItem key="manager" value="manager">
        <div>manager</div>
      </MenuItem>,
      <MenuItem key="user" value="user">
        <div>user</div>
      </MenuItem>,
    ];
  }
  return [
    <MenuItem key="user" value="user">
      <div>user</div>
    </MenuItem>,
  ];
};

const RegisterForm = () => {
  const d = useContext(AppContext).dataUser;
  const p = d.pageUsers;
  const u = d.process;
  const [familyName, setFamilyName] = useState("");
  const [remarks, setRemarks] = useState("");
  const [userId, setUserId] = useState("");
  const [givenName, setGivenName] = useState("");
  const [accountType, setAccountType] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [openMsgDialog, setOpenMsgDialog] = useState(false);

  const handleClose = () => {
    p.bShowRegisterDlg = false;
    setFamilyName("");
    setRemarks("");
    setUserId("");
    setGivenName("");
    setAccountType("");
    setMessage("");
    p.changed();
  };

  const handlefamilyName = (e: any) => {
    setFamilyName(e.target.value);
    setMessage("");
  };

  const handleRemarks = (e: any) => {
    setRemarks(e.target.value);
    setMessage("");
  };

  const handleUserId = (e: any) => {
    setUserId(e.target.value);
    setMessage("");
  };

  const handleGivenName = (e: any) => {
    setGivenName(e.target.value);
    setMessage("");
  };

  const handleAccountType = (e: any) => {
    setAccountType(e.target.value);
    setMessage("");
  };

  const handleRegister = () => {
    if (familyName === "") {
      setMessage("姓を入力してください。");
      return;
    }
    if (givenName === "") {
      setMessage("名を入力してください。");
      return;
    }
    if (accountType === "") {
      setMessage("権限を選んでください。");
      return;
    }
    if (userId === "") {
      setMessage("メールアドレスを入力してください。");
      return;
    }
    if (!validate(userId)) {
      setMessage("有効なメールアドレスを入力してください。");
      return;
    }
    setLoading(true);
    d.process
      .Register(
        new User({
          family_name: familyName.replaceAll(`"`, `\\"`),
          Remarks: remarks.replaceAll(`"`, `\\"`),
          UserId: userId,
          given_name: givenName.replaceAll(`"`, `\\"`),
          AccountType: accountType,
        })
      )
      .then(() => {
        handleClose();
        setOpenMsgDialog(true);
      })
      .catch((err: Error) => {
        console.log(err);
        setMessage(
          "登録済みのメールアドレスを使用している可能性があります。メールアドレスをご確認の上、再度失敗した場合は管理者にご連絡ください。"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Dialog key="register" open={p.bShowRegisterDlg}>
        <FormDlg noValidate>
          <DialogTitle>アカウントの追加</DialogTitle>
          <DialogContent>
            <TextField
              variant="filled"
              margin="normal"
              fullWidth
              required
              id="familyName"
              label="姓"
              name="familyName"
              onChange={handlefamilyName}
              inputProps={{ maxLength: 128 }}
              value={familyName}
            />
            <TextField
              variant="filled"
              margin="normal"
              fullWidth
              required
              id="givenName"
              label="名"
              name="givenName"
              onChange={handleGivenName}
              inputProps={{ maxLength: 128 }}
              value={givenName}
            />
            <TextField
              variant="filled"
              margin="normal"
              fullWidth
              required
              id="userId"
              label="メールアドレス"
              name="userId"
              onChange={handleUserId}
              inputProps={{ maxLength: 128 }}
              value={userId}
            />
            <FormControl fullWidth variant="outlined">
              <InputLabel id="user_kind_label">権限</InputLabel>
              <Select
                labelId="user_kind_label"
                id="user_kind_id"
                value={accountType}
                label="権限"
                onChange={handleAccountType}
              >
                {AccountTypeMenus(u.loginUser.AccountType)}
              </Select>
            </FormControl>
            <TextField
              variant="filled"
              margin="normal"
              fullWidth
              id="remarks"
              label="備考"
              name="remarks"
              onChange={handleRemarks}
              inputProps={{ maxLength: 128 }}
              value={remarks}
            />
            {message !== "" ? <ErrorText>{message}</ErrorText> : null}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <ButtonDlg
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleRegister}
                loading={loading}
              >
                確定
              </ButtonDlg>
              <ButtonDlg
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleClose}
              >
                キャンセル
              </ButtonDlg>
            </div>
          </DialogContent>
        </FormDlg>
      </Dialog>
      <Dialog
        open={openMsgDialog}
        onClose={() => setOpenMsgDialog(false)}
        aria-labelledby="msg-dialog-title"
      >
        <DialogTitle>確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            追加したユーザーのメールアドレスに仮パスワードを送信しました。
            <br />
            ログイン画面から送信したパスワードでログインしてください。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMsgDialog(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const DetailForm = () => {
  const d = useContext(AppContext).dataUser;
  const p = d.pageUsers;
  const [message, setMessage] = useState("");
  const [bShowConfirmDelete, setShowConfirmDelete] = useState(false);
  const [loadingApply, setLoadingApply] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  if (d.process.iSelectedUser === -1) {
    return null;
  }

  const edit = d.process.edit; // 編集用
  const selected = d.process.users[d.process.iSelectedUser];

  const handleClose = () => {
    p.bShowDetailDlg = false;
    p.changed();
  };

  const handleDelete = () => {
    setLoadingDelete(true);
    setShowConfirmDelete(false);
    d.process
      .Delete(selected)
      .then(() => {
        p.bShowDetailDlg = false;
        p.changed();
      })
      .finally(() => {
        setLoadingDelete(false);
      });
  };

  const handleChangeRemarks = (e: any) => {
    edit.Remarks = e.target.value;
    setMessage("");
    p.changed();
  };

  const handleApply = () => {
    setLoadingApply(true);
    d.process
      .EditUser()
      .then(() => {
        p.bShowDetailDlg = false;
        p.changed();
      })
      .finally(() => {
        setLoadingApply(false);
      });
  };

  const remarks = p.bShowDetailDlg ? (edit.Remarks ?? selected.Remarks) : "";

  return (
    <>
      <Dialog key="detail" open={p.bShowDetailDlg}>
        <FormDlg noValidate>
          <DialogTitle>詳細情報編集</DialogTitle>
          <DialogContent>
            <TextField
              variant="filled"
              margin="normal"
              fullWidth
              disabled
              id={String("姓")}
              label={"姓"}
              name="familyName"
              value={selected ? selected.family_name : ""}
            />
            <TextField
              variant="filled"
              margin="normal"
              fullWidth
              disabled
              id={String("名")}
              label={"名"}
              name="givenName"
              value={selected ? selected.given_name : ""}
            />
            <TextField
              variant="filled"
              margin="normal"
              fullWidth
              disabled
              id="user_kind"
              label={"権限"}
              name="user_kind"
              value={selected ? selected.AccountType : ""}
            />
            <TextField
              variant="filled"
              margin="normal"
              fullWidth
              id={String("備考")}
              label={"備考"}
              name="Remarks"
              value={remarks}
              onChange={handleChangeRemarks}
              inputProps={{ maxLength: 128 }}
            />
            {message !== "" ? <ErrorText>{message}</ErrorText> : null}
            <NoteText>アカウント削除</NoteText>
            <DialogActions>
              <ButtonDlg
                variant="contained"
                color="secondary"
                onClick={() => setShowConfirmDelete(true)}
                loading={loadingDelete}
              >
                アカウント削除
              </ButtonDlg>
            </DialogActions>
            <Divider variant="middle" />
            <DialogActions>
              <ButtonDlg
                variant="contained"
                color="primary"
                onClick={handleApply}
                loading={loadingApply}
              >
                適用
              </ButtonDlg>
              <ButtonDlg
                variant="contained"
                color="primary"
                onClick={handleClose}
              >
                キャンセル
              </ButtonDlg>
            </DialogActions>
          </DialogContent>
        </FormDlg>
      </Dialog>
      <ConfirmDialog
        bShow={bShowConfirmDelete}
        title="確認"
        description={`${selected ? `${selected.family_name}${selected.given_name}` : ""}を削除しますか？`}
        onApply={handleDelete}
        onCancel={() => setShowConfirmDelete(false)}
        titleButtonApply="削除"
        titleButtonCancel="キャンセル"
      />
    </>
  );
};

const AccountBody = () => {
  const a = useContext(AppContext).account;
  const d = useContext(AppContext).dataUser;
  const p = d.pageUsers;
  const locations = d.process.locations.locations;

  const { MessageDialog, openMessageDialog } = useMessageDialog();
  const [dialogMessage, setDialogMessage] = useState("");

  const [loading, setLoading] = useState(new Map<string, boolean>());

  const handleDetailDlgOpen = (i: number) => {
    if (a.IsUserViewer()) {
      return;
    }
    d.process.iSelectedUser = i;
    d.process.edit = { UserId: d.process.users[i].UserId };
    p.bShowDetailDlg = true;
    p.changed();
  };

  const handleBuildingClick = async (user: User, building: BuildingInfo) => {
    // 対象のチェック状態反転
    if (!user.LinkBuildings.find((id: string) => id === building.BuildingId)) {
      // 紐付けリストに追加
      user.LinkBuildings.push(building.BuildingId);
    } else {
      if (user.LinkBuildings.length <= 1) {
        // 建物紐付けがなくなる場合はブロック
        setDialogMessage("建物の紐付けを 0件にすることはできません。");
        await openMessageDialog();
        return;
      } else {
        // 紐付けリストから削除
        user.LinkBuildings = user.LinkBuildings.filter(
          (id: string) => id !== building.BuildingId
        );
      }
    }

    // 更新処理
    const k = `${user.UserId}_${building.BuildingId}`;
    loading.set(k, true);
    setLoading(new Map<string, boolean>(loading));
    d.process.edit = { UserId: user.UserId, LinkBuildings: user.LinkBuildings };
    d.process
      .EditUser()
      .then(() => {
        p.changed();
      })
      .finally(() => {
        loading.set(k, false);
        setLoading(new Map<string, boolean>(loading));
      });
  };

  const rows = d.process.users
    .slice(
      p.list.page * p.list.rowsPerPage,
      p.list.page * p.list.rowsPerPage + p.list.rowsPerPage
    )
    .map((row: User, i: number) => {
      const index = p.list.page * p.list.rowsPerPage + i;
      // 管理する建物一覧
      const buildings = locations.map((building: BuildingInfo, bi: number) => {
        return (
          <TableCell key={columns[3].id + bi} align="center">
            {row.LinkBuildings.find(
              (id: string) => id === building.BuildingId
            ) ? (
              <ButtonChecked
                disabled={row.AccountType === "sub_admin"}
                onClick={() => handleBuildingClick(row, building)}
                loading={loading.get(`${row.UserId}_${building.BuildingId}`)}
              >
                {"✔"}
              </ButtonChecked>
            ) : (
              <ButtonUnChecked
                disabled={row.AccountType === "sub_admin"}
                onClick={() => handleBuildingClick(row, building)}
                loading={loading.get(`${row.UserId}_${building.BuildingId}`)}
              >
                {"　"}
              </ButtonUnChecked>
            )}
          </TableCell>
        );
      });
      return (
        <TableRow hover role="checkbox" tabIndex={-1} key={row.UserId}>
          <StatickyCell
            key={columns[1].id + i}
            align="center"
            onClick={() => handleDetailDlgOpen(index)}
            sx={{
              cursor: "pointer",
              wordBreak: "keep-all",
              overflowWrap: "anywhere",
            }}
          >
            {row.family_name}
            <wbr />
            {row.given_name}
            <wbr />
            {row.UserStatus !== "CONFIRMED" ? "[未ログイン]" : null}
          </StatickyCell>
          <TableCell
            key={columns[2].id + i}
            align="center"
            onClick={() => handleDetailDlgOpen(index)}
            sx={{ cursor: "pointer" }}
          >
            {row.AccountType}
          </TableCell>
          {buildings}
          <TableCell
            key={columns[4].id + i}
            align="center"
            onClick={() => handleDetailDlgOpen(index)}
            sx={{ cursor: "pointer" }}
          >
            {row.Remarks}
          </TableCell>
        </TableRow>
      );
    });

  return (
    <TableBody>
      {rows}
      <MessageDialog message={dialogMessage} />
    </TableBody>
  );
};

const AccountTable = () => {
  const d = useContext(AppContext).dataUser;
  const p = d.pageUsers;
  const l = d.process.locations.locations.sort((a, b) =>
    a.BuildingName.localeCompare(b.BuildingName)
  );
  const windowHeigth = useWindowSize().height;
  const isSmartphone = useMediaQuery({
    query: "(max-width: 600px)",
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    p.list.page = newPage;
    p.changed();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    p.list.rowsPerPage = +event.target.value;
    p.list.page = 0;
    p.changed();
  };

  const header = [
    <ColorHeader
      key={columns[1].id}
      align="center"
      style={{
        minWidth: isSmartphone
          ? columns[1].minWidthSmartphone
          : columns[1].minWidthPC,
        position: "sticky",
        left: 0,
        zIndex: 1000,
      }}
    >
      {columns[1].label}
    </ColorHeader>,
    <ColorHeader
      key={columns[2].id}
      align="center"
      style={{
        minWidth: isSmartphone
          ? columns[2].minWidthSmartphone
          : columns[2].minWidthPC,
      }}
    >
      {columns[2].label}
    </ColorHeader>,
  ];
  header.push(
    ...l.map((building: BuildingInfo, bi: number) => (
      <ColorHeader
        key={columns[3].id + bi}
        align="center"
        style={{
          minWidth: isSmartphone
            ? columns[3].minWidthSmartphone
            : columns[3].minWidthPC,
        }}
      >
        {building.BuildingName}
      </ColorHeader>
    ))
  );
  header.push(
    <ColorHeader
      key={columns[4].id}
      align="center"
      style={{
        minWidth: isSmartphone
          ? columns[4].minWidthSmartphone
          : columns[4].minWidthPC,
      }}
    >
      {columns[4].label}
    </ColorHeader>
  );

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ height: `${windowHeigth - 330}px` }}>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead style={{ whiteSpace: "nowrap" }}>
            <TableRow>{header}</TableRow>
          </TableHead>
          <AccountBody />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={d.process.users.length}
        rowsPerPage={p.list.rowsPerPage}
        page={p.list.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

const Contents = () => {
  const a = useContext(AppContext).account;
  const d = useContext(AppContext).dataUser;
  const [pageLoading, setPageLoading] = useState(false);
  const p = d.pageUsers;
  useModel(p);

  useEffect(() => {
    p.list.rowsPerPage = 10;
    p.list.page = 0;
    setPageLoading(true);
    d.process.Clear();
    d.process
      .Load()
      .then(() => {
        p.changed();
      })
      .finally(() => {
        setPageLoading(false);
      });
    return () => {
      d.process.Clear();
    };
  }, [d.process, p]);

  const handleRegisterDlgOpen = () => {
    p.bShowRegisterDlg = true;
    p.changed();
  };

  return (
    <>
      {a.IsUserViewer() ? (
        <div style={{ marginTop: "16px" }} /> // スペーサー
      ) : (
        <ButtonDlg
          variant="contained"
          color="primary"
          onClick={handleRegisterDlgOpen}
          startIcon={<AddCircle />}
        >
          アカウントの追加
        </ButtonDlg>
      )}
      <AccountTable />
      <RegisterForm />
      <DetailForm />

      <Backdrop open={pageLoading} sx={{ zIndex: 1201 }}>
        <CircularProgress sx={{ color: "white" }} />
      </Backdrop>
    </>
  );
};

const AccountSettingPage = () => {
  return (
    <PageTemplate title="アカウント設定">
      <BreadCrumbList
        items={[
          new BreadCrumbItem("TOP", "/top/"),
          new BreadCrumbItem("アカウント設定"),
        ]}
      />
      <Contents />
    </PageTemplate>
  );
};

export default AccountSettingPage;
